import { type EnvVarSource, recordEnvFailure } from './envCollector';

// Centralized environment helpers usable for both server and public code.
// Logging policy:
// - Always log failures with console.error (source, name, reason)
// - For public: log found values to aid debugging
// - For server: never log sensitive values, only presence

const get = (name: string) => process.env[name];

const formatReason = (reason?: string) => (reason ? ` (${reason})` : '');

// ANSI colors for terminal output
const colors = {
  green: (s: string) => `\x1b[32m${s}\x1b[0m`,
  red: (s: string) => `\x1b[31m${s}\x1b[0m`,
  yellow: (s: string) => `\x1b[33m${s}\x1b[0m`,
};

export const createEnvHelpers = (source: EnvVarSource) => {
  const isPublic = source === 'public';

  const fail = (name: string, reason?: string) => {
    recordEnvFailure(name, reason, source);
    // Unified error logging (RED)
    console.error(
      colors.red(
        `[env:${source}] Missing or invalid environment variable: ${name}${formatReason(
          reason
        )}`
      )
    );
    // Skip throwing errors during Fly build, similar to next.config.ts logic
    if (process.env.FLY_BUILD === 'true') {
      console.warn(
        colors.yellow(
          `[env:${source}] Skipping error throw during Fly build for: ${name}${formatReason(
            reason
          )}`
        )
      );
      return '';
    }
    throw new Error(
      `Missing or invalid environment variable: ${name}${formatReason(reason)}`
    );
  };

  const requireString = (name: string) => {
    const v = get(name);
    if (!v) {
      return fail(name);
    }
    if (isPublic) {
      // For public envs we can safely log the value to aid debugging (GREEN)
      console.log(
        colors.green(`[env:public] Found public env var: ${name} ${v}`)
      );
    } else {
      // Avoid leaking secrets in logs on server (GREEN)
      console.log(
        colors.green(`[env:server] Found server env var: ${name} (present)`)
      );
    }
    return v;
  };

  const requireUrl = (name: string) => {
    const v = requireString(name);
    try {
      new URL(v);
      console.log(
        colors.green(
          `[env:${source}] URL validated for ${name} ${isPublic ? v : '(redacted)'}`
        )
      );
      return v;
    } catch {
      return fail(name, 'must be a valid URL');
    }
  };

  const requireInt = (name: string) => {
    const v = requireString(name);
    const n = Number(v);
    if (!Number.isInteger(n)) {
      return fail(name, 'must be an integer');
    }
    console.log(
      colors.green(`[env:${source}] Integer validated for ${name} ${n}`)
    );
    return n;
  };

  const optionalString = (name: string) => {
    const v = get(name);
    if (v == null) {
      console.log(
        colors.yellow(`[env:${source}] Optional env var not set: ${name}`)
      );
      return undefined;
    }
    if (isPublic) {
      console.log(
        colors.green(`[env:public] Optional public env var: ${name} ${v}`)
      );
    } else {
      console.log(
        colors.green(`[env:server] Optional server env var: ${name} (present)`)
      );
    }
    return v;
  };

  const optionalUrl = (name: string) => {
    const v = get(name);
    if (!v) {
      console.log(
        colors.yellow(`[env:${source}] Optional URL not set: ${name}`)
      );
      return undefined;
    }
    try {
      new URL(v);
      console.log(
        colors.green(
          `[env:${source}] Optional URL validated for ${name} ${isPublic ? v : '(redacted)'}`
        )
      );
      return v;
    } catch {
      console.warn(
        colors.yellow(
          `[env:${source}] Optional URL invalid for ${name} ${isPublic ? v : '(redacted)'}`
        )
      );
      return undefined;
    }
  };

  return {
    // Keep keys sorted (perfectionist)
    fail,
    get,
    optionalString,
    optionalUrl,
    requireInt,
    requireString,
    requireUrl,
  };
};
